import { gql, useMutation, useQuery } from '@zenbusiness/application-commons-graphql';
import useAsyncCallback from '@zenbusiness/application-commons-hooks/useAsyncCallback';
import { useIdentity } from '@zenbusiness/application-commons-identity';
import Title from '@zenbusiness/common-components/Title';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Box } from '@mui/system';

import AnimatedLoader from '#client/components/AnimatedLoader';
import HideIfWebView from '#client/components/HideIfWebView';

import LessonStarRating from '../LessonStarRating';

import {
  LeftSideWord,
  RightSideWord,
  TitleContainer,
  LessonCompleteTitle,
  Sticker,
  BottomTitle,
  LottieContainer,
  PrimaryButton,
  SecondaryButton
} from './component.styles';

export const GET_LESSON_RATING = gql`
query GetLessonRating($accountUuid: UUID!, $lesson: String!) {
  lesson(accountUuid: $accountUuid, lesson: $lesson) {
    userScore
  }
}`;
export const RATE_LESSON = gql`
  mutation RateLesson($accountUuid: UUID!, $lesson: String!, $score: Int!) {
    rateLesson(accountUuid: $accountUuid, lesson: $lesson, score: $score) {
      status
    }
  }
`;

const LessonComplete = ({
  onPrimaryClick,
  onSecondaryClick
}) => {
  const { accountUuid } = useIdentity();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const isPC = useMediaQuery(theme.breakpoints.up('lg'));

  const widthOfSideImages = (isMobile && 300) || (isTablet && 500) || (isPC && 1000) || 1000;
  const spaceInCentralContent = (isMobile && 0.5) || (isTablet && 0.5) || (isPC && 2) || 2;
  const lottieSize = (isMobile && 200) || 300;

  const { lesson } = useParams();

  const { data } = useQuery(
    GET_LESSON_RATING,
    {
      variables: {
        accountUuid,
        lesson
      }
    }
  );

  const [rateLesson] = useMutation(RATE_LESSON);

  const rateLessonHandler = useAsyncCallback((score) => rateLesson({
    variables: {
      accountUuid,
      lesson,
      score
    }
  }), [accountUuid, lesson, rateLesson]);

  return (
    <Box sx={{ width: '100%' }}>
      <LeftSideWord src="/illustration/left.png" width={widthOfSideImages} />
      <RightSideWord src="/illustration/right.png" width={widthOfSideImages} />
      <Sticker src="/illustration/j2s-sticker.svg" alt="education-studio-sticker" width={140} />
      <Grid sx={{ position: 'absolute' }} container direction="column" alignItems="center" spacing={spaceInCentralContent}>
        <Grid item>
          <TitleContainer>
            { isMobile ? (
              <>
                <Title level={1} variant="extraHuge">
                  Lesson
                </Title>
                <BottomTitle level={1} variant="extraHuge">
                  Complete!
                </BottomTitle>
              </>
            ) : (
              <LessonCompleteTitle level={1} variant="extraHuge">Lesson Complete!</LessonCompleteTitle>
            )}
          </TitleContainer>
        </Grid>
        <Grid sx={{ zIndex: 1400 }} item>
          <LessonStarRating userRating={data?.lesson?.userScore || 0} onClick={rateLessonHandler} />
        </Grid>
        <LottieContainer item>
          <AnimatedLoader url="/lottie/ZB_Congrats.json" width={lottieSize} height={lottieSize} />
        </LottieContainer>
        <Grid item container direction="column" alignItems="center" spacing={1}>
          <Grid item>
            <PrimaryButton
              onClick={onPrimaryClick}
              variant="contained"
              color="primary"
              size="large"
            >
              I&apos;m done
            </PrimaryButton>
          </Grid>
          <Grid item>
            <HideIfWebView>
              <SecondaryButton
                onClick={onSecondaryClick}
                variant="contained"
                color="primary"
                size="large"
              >
                Next Lesson
              </SecondaryButton>
            </HideIfWebView>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

LessonComplete.propTypes = {
  onPrimaryClick: PropTypes.func,
  onSecondaryClick: PropTypes.func
};

export default LessonComplete;
